import { PlagiarismReport } from '../../services/PlagiarismReportService';
import { AttachmentData, DrawingData } from './types';

export enum TypeKeys {
    TOGGLE_EXTERNAL_DELIVERY_LOAD = 'submission/TOGGLE_EXTERNAL_DELIVERY_LOAD',
    LOAD_ATTACHMENT_DATA = 'submission/LOAD_ATTACHMENT_DATA',
    LOAD_DRAWING_DATA = 'submission/LOAD_DRAWING_DATA',
    FETCH_PLAGIARISM_REPORT = 'submission/FETCH_PLAGIARISM_REPORT',
    PLAGIARISM_REPORT_FETCHED = 'submission/PLAGIARISM_REPORT_FETCHED',
    LOAD_ATTACHMENT_URL = 'submission/LOAD_ATTACHMENT_URL',
    TAB_CHANGE = 'submission/TAB_CHANGE',
    REQUEST_PLAGIARISM_REPORT_CHECK = 'submission/REQUEST_PLAGIARISM_REPORT_CHECK',
    LOAD_PANES_LIST = 'submission/LOAD_PANES_LIST',
    TOGGLE_QUESTION_TEXT_IN_SUBMISSION = 'submission/TOGGLE_QUESTION_TEXT_IN_SUBMISSION',
    SET_USER_RESPONSE_SUBMISSION_DATA = 'submission/SET_USER_RESPONSE_SUBMISSION_DATA',
    RESET_SUBMISSION_DATA = 'submission/RESET_SUBMISSION_DATA',
    PRINT_COMMENTS = 'submission/PRINT_COMMENTS',
    DOWNLOAD_AND_PRINT_SUBMISSION = 'submission/DOWNLOAD_AND_PRINT_SUBMISSION',
    DOWNLOAD_AND_PRINT_QUESTION_ALL_SUBMISSION = 'submission/DOWNLOAD_AND_PRINT_QUESTION_ALL_SUBMISSION',
    ACCEPT_EULA = 'submission/ACCEPT_EULA',
    ON_PLAGIARISM_STATUS_UPDATE = 'submission/ON_PLAGIARISM_STATUS_UPDATE',
    DOWNLOAD_SUBMISSION_FILE = 'submission/DOWNLOAD_SUBMISSION_FILE',
    CONVERTED_PDF_DATA = 'submission/CONVERTED_PDF_DATA',
    SET_CANDIDATE_SUBMISSION_ACCESS_LEVEL = 'submission/SET_CANDIDATE_SUBMISSION_ACCESS_LEVEL',
}

export interface SetCandidateSubmissionAccessLevelAction {
    type: TypeKeys.SET_CANDIDATE_SUBMISSION_ACCESS_LEVEL;
    featureId: string;
    candidateSubmissionAccessLevel?: string;
}

export interface SetUserResponseDataAction {
    type: TypeKeys.SET_USER_RESPONSE_SUBMISSION_DATA;
    featureId: string;
    url: string;
    mimeType: string;
    candidateResponseId?: number;
}

export interface ResetSubmissionDataAction {
    type: TypeKeys.RESET_SUBMISSION_DATA;
    featureId: string;
}

export interface ConvertedPdfData {
    type: TypeKeys.CONVERTED_PDF_DATA;
    url: string;
    convertedCandidateResponseId: number;
}

export interface LoadPanesListAction {
    type: TypeKeys.LOAD_PANES_LIST;
    featureId: string;
    panes: string[];
}

export interface ToggleExternalDeliveryLoad {
    type: TypeKeys.TOGGLE_EXTERNAL_DELIVERY_LOAD;
    featureId: string;
}

export interface LoadAttachmentData {
    type: TypeKeys.LOAD_ATTACHMENT_DATA;
    data: AttachmentData;
    featureId: string;
}

export interface LoadDrawingData {
    type: TypeKeys.LOAD_DRAWING_DATA;
    data: DrawingData;
    featureId: string;
}

export interface LoadAttachmentUrlAction {
    type: TypeKeys.LOAD_ATTACHMENT_URL;
    presignedAWSUrl: string;
    featureId: string;
}

export interface TabChangeAction {
    type: TypeKeys.TAB_CHANGE;
    featureId: string;
    activeTabId: string;
}

export interface FetchPlagiarismReportAction {
    type: TypeKeys.FETCH_PLAGIARISM_REPORT;
    featureId: string;
}

export interface PlagiarismReportFetchedAction {
    type: TypeKeys.PLAGIARISM_REPORT_FETCHED;
    plagiarismReport: PlagiarismReport;
    eulaData: any;
    featureId: string;
}

export interface RequestPlagiarismReportAction {
    type: TypeKeys.REQUEST_PLAGIARISM_REPORT_CHECK;
    featureId: string;
}

export interface ToggleQuestionTextInSubmissionAction {
    type: TypeKeys.TOGGLE_QUESTION_TEXT_IN_SUBMISSION;
    featureId: string;
    newValue?: boolean;
}

export interface AcceptEula {
    featureId: string;
    type: TypeKeys.ACCEPT_EULA;
}

export interface PrintComments {
    type: TypeKeys.PRINT_COMMENTS;
    featureId: string;
}

export interface DownloadAndPrintSubmission {
    type: TypeKeys.DOWNLOAD_AND_PRINT_SUBMISSION;
    featureId: string;
}

export interface DownloadAndPrintSubmissionAll {
    type: TypeKeys.DOWNLOAD_AND_PRINT_QUESTION_ALL_SUBMISSION;
    featureId: string;
}

export interface DownloadSubmissionFile {
    type: TypeKeys.DOWNLOAD_SUBMISSION_FILE;
    featureId: string;
    isPdf?: boolean;
}

export type ActionTypes = | ToggleExternalDeliveryLoad | LoadAttachmentData | LoadDrawingData | PrintComments |
    TabChangeAction | FetchPlagiarismReportAction | PlagiarismReportFetchedAction | DownloadAndPrintSubmission |
    RequestPlagiarismReportAction | LoadAttachmentUrlAction | LoadPanesListAction |
    ToggleQuestionTextInSubmissionAction | SetUserResponseDataAction | ResetSubmissionDataAction | ConvertedPdfData |
    SetCandidateSubmissionAccessLevelAction;

export const setCandidateSubmissionAccessLevel = (
    featureId: string,
    candidateSubmissionAccessLevel?: string
): SetCandidateSubmissionAccessLevelAction => ({
    type: TypeKeys.SET_CANDIDATE_SUBMISSION_ACCESS_LEVEL,
    featureId,
    candidateSubmissionAccessLevel,
});

export const resetSubmissionData = (featureId: string): ResetSubmissionDataAction => ({
    featureId,
    type: TypeKeys.RESET_SUBMISSION_DATA,
});

export const setUserResponseData = (featureId: string, url: string, mimeType: string, candidateResponseId?: number): SetUserResponseDataAction => ({
    featureId,
    url,
    mimeType,
    candidateResponseId,
    type: TypeKeys.SET_USER_RESPONSE_SUBMISSION_DATA,
});

export const loadPanesList = (featureId: string, panes: string[]): LoadPanesListAction => ({
    featureId,
    panes,
    type: TypeKeys.LOAD_PANES_LIST,
});

export const loadAttachmentUrl = (featureId: string, presignedAWSUrl: string): LoadAttachmentUrlAction => ({
    featureId,
    presignedAWSUrl,
    type: TypeKeys.LOAD_ATTACHMENT_URL,
});

export const loadAttachmentData = (featureId: string, data: any): LoadAttachmentData => ({
    featureId,
    data,
    type: TypeKeys.LOAD_ATTACHMENT_DATA,
});

export const loadDrawingData = (featureId: string, data: DrawingData): LoadDrawingData => ({
    featureId,
    data,
    type: TypeKeys.LOAD_DRAWING_DATA,
});

export const toggleExternalDeliveryLoad = (featureId: string): ToggleExternalDeliveryLoad => ({
    featureId,
    type: TypeKeys.TOGGLE_EXTERNAL_DELIVERY_LOAD,
});

export const tabChange = (featureId: string, activeTabId: string): TabChangeAction => ({
    featureId,
    activeTabId,
    type: TypeKeys.TAB_CHANGE,
});

export const fetchPlagiarismReport = (featureId: string): FetchPlagiarismReportAction => ({
    featureId,
    type: TypeKeys.FETCH_PLAGIARISM_REPORT,
});

export const plagiarismReportFetched = (featureId: string, plagiarismReport: PlagiarismReport, eulaData: any):
    PlagiarismReportFetchedAction => ({
    featureId,
    plagiarismReport,
    eulaData,

    type: TypeKeys.PLAGIARISM_REPORT_FETCHED,
});

export const requestPlagiarismReportCheck = (featureId: string): RequestPlagiarismReportAction => ({
    featureId,
    type: TypeKeys.REQUEST_PLAGIARISM_REPORT_CHECK,
});

export const toggleQuestionTextInSubmission = (featureId: string, newValue?: boolean): ToggleQuestionTextInSubmissionAction => ({
    featureId,
    newValue,
    type: TypeKeys.TOGGLE_QUESTION_TEXT_IN_SUBMISSION,
});

export const printComments = (featureId: string): PrintComments => ({
    featureId,
    type: TypeKeys.PRINT_COMMENTS,
});

export const downloadAndPrintSubmission = (featureId: string): DownloadAndPrintSubmission => ({
    featureId,
    type: TypeKeys.DOWNLOAD_AND_PRINT_SUBMISSION,
});

export const downloadAndPrintQuestionAllSubmissions = (featureId: string): DownloadAndPrintSubmissionAll => ({
    featureId,
    type: TypeKeys.DOWNLOAD_AND_PRINT_QUESTION_ALL_SUBMISSION,
});

export const acceptEula = (featureId: string): AcceptEula => ({
    featureId,
    type: TypeKeys.ACCEPT_EULA,
});

export const onPlagiarismStatusUpdate = () => ({
    type: TypeKeys.ON_PLAGIARISM_STATUS_UPDATE,
});

export const downloadSubmissionFile = (featureId: string, isPdf?: boolean): DownloadSubmissionFile => ({
    featureId,
    isPdf,
    type: TypeKeys.DOWNLOAD_SUBMISSION_FILE,
});

export const setConvertedPdfData = (url: string, convertedCandidateResponseId: number): ConvertedPdfData => ({
    url,
    convertedCandidateResponseId,
    type: TypeKeys.CONVERTED_PDF_DATA,
});
